import { jsx, Fragment } from 'react/jsx-runtime';
import { useRef, useMemo, useEffect } from 'react';
import { carouselSlideStyles } from './CarouselSlide.styles.esm.js';
import { isDesktopSingleSlide, hasMultipleItems } from './utils.esm.js';
import { cn } from '../../utils/cn.esm.js';

function CarouselSlides({ animation, slideGroups, slides, itemsInView, deviceType, loop, emblaApi, selectedIndex = 0, showItemPreview = true, flexibleWidth = false, itemGap = 16, }) {
    const isDesktopSingleItem = isDesktopSingleSlide(itemsInView, deviceType);
    const isMultipleItems = hasMultipleItems(itemsInView);
    const styles = carouselSlideStyles();
    function getFocusableElements(container) {
        return Array.from(container.querySelectorAll("button, a, input, select, textarea, [tabindex]:not([tabindex='-1'])"));
    }
    const slideRefs = useRef([]);
    const visibleSlideIndices = useMemo(() => {
        if (!emblaApi) {
            const defaultVisible = new Set();
            for (let i = 0; i < itemsInView; i++) {
                defaultVisible.add(i);
            }
            return defaultVisible;
        }
        const visibleIndices = new Set();
        const startIndex = selectedIndex * itemsInView;
        for (let i = 0; i < itemsInView; i++) {
            let slideIndex = startIndex + i;
            if (loop) {
                slideIndex = slideIndex % slides.length;
            }
            if (slideIndex >= 0 && slideIndex < slides.length) {
                visibleIndices.add(slideIndex);
            }
        }
        return visibleIndices;
    }, [selectedIndex, itemsInView, slides.length, loop, emblaApi]);
    useEffect(() => {
        slideRefs.current.forEach((slide, index) => {
            if (!slide)
                return;
            const isVisible = visibleSlideIndices.has(index);
            const focusableElements = getFocusableElements(slide);
            focusableElements.forEach((element) => {
                if (isVisible) {
                    // Restore original tabIndex for visible slides
                    element.removeAttribute("tabIndex");
                }
                else {
                    // Disable focusability for non-visible slides
                    element.setAttribute("tabIndex", "-1");
                }
            });
        });
    }, [visibleSlideIndices]);
    const calculatedSlideWidth = useMemo(() => {
        // For flexible width, don't constrain width - let items use their natural width
        if (flexibleWidth && animation === "scroll") {
            return "auto";
        }
        // Original fixed-width calculation
        const gapSize = itemGap || 16;
        const totalGaps = (itemsInView - 1) * gapSize;
        const basePercentage = 100 / itemsInView;
        if (showItemPreview) {
            // With preview: include sliver size
            const sliverSize = 48;
            return `calc(${basePercentage}% - ${(totalGaps + sliverSize) / itemsInView / 16}rem)`;
        }
        else {
            // Without preview: exact equal width
            return `calc(${basePercentage}% - ${totalGaps / itemsInView / 16}rem)`;
        }
    }, [itemsInView, showItemPreview, flexibleWidth, animation, itemGap]);
    // Special case for fade animation - use grid layout
    if (animation === "fade") {
        return (jsx(Fragment, { children: slideGroups.map((group, groupIndex) => (jsx("div", { className: styles.group(), children: jsx("div", { className: styles.grid({
                        isMultipleItems,
                        animation,
                    }), style: {
                        gridTemplateColumns: `repeat(${Math.min(itemsInView, group.length)}, 1fr)`,
                    }, children: group.map((slide, slideIndex) => {
                        const globalIndex = groupIndex * itemsInView + slideIndex;
                        const isVisible = visibleSlideIndices.has(globalIndex);
                        return (jsx("div", { className: cn(styles.slide({ isDesktopSingleItem, animation })), "aria-hidden": !isVisible, tabIndex: -1, children: slide.content }, slide.id));
                    }) }) }, groupIndex))) }));
    }
    return (jsx(Fragment, { children: slides.map((slide, index) => {
            // Always add margin except last slide (if not looping)
            const shouldHaveMargin = !loop ? slides.length - 1 > index : true;
            const isVisible = visibleSlideIndices.has(index);
            const isFlexibleMode = flexibleWidth && animation === "scroll";
            return (jsx("div", { className: cn(styles.slide({ animation, isDesktopSingleItem }), shouldHaveMargin &&
                    !isFlexibleMode &&
                    "iris:mr-(--comp-space-gap-carousel-item)", isFlexibleMode && "iris:flex-shrink-0"), ref: (el) => (slideRefs.current[index] = el), style: Object.assign({ width: calculatedSlideWidth }, (isFlexibleMode && {
                    marginRight: shouldHaveMargin ? `${itemGap || 16}px` : 0,
                })), "aria-hidden": !isVisible, tabIndex: -1, "data-carousel-slide": isFlexibleMode ? "true" : undefined, children: slide.content }, slide.id));
        }) }));
}

export { CarouselSlides };
